'use client';

import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button/buttonExperience';
import { Image } from '@/components/ui/image/image';
import { cn } from '@/lib/utils';
import { ListingItem } from './listing';
import styles from './listingFilters.module.scss';

export type FilterKey = string;

export type FilterConfig = {
  key: FilterKey;
  label: string;
};

type FiltersState = Record<string, string[]>;

type ListingFiltersProps = {
  items: ListingItem[];
  mobile: boolean;
  filters: FilterConfig[];
  onFilterChange: (filteredItems: ListingItem[]) => void;
  onClearFilters: () => void;
  resetKey?: number;
};

const buildEmptyFilters = (filters: FilterConfig[]): FiltersState =>
  Object.fromEntries(filters.map(({ key }) => [key, []]));

export const ListingFilters = ({
  items,
  mobile,
  filters,
  onFilterChange,
  onClearFilters,
  resetKey = 0,
}: ListingFiltersProps) => {
  const emptyFilters = useMemo(() => buildEmptyFilters(filters), [filters]);
  const [activeFilters, setActiveFilters] = useState<FiltersState>(emptyFilters);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  /**
   * Build available filter options dynamically from items
   */
  const filterOptions = useMemo(() => {
    const options: Partial<Record<FilterKey, Record<string, number>>> = {};

    filters.forEach(({ key }) => {
      if (!options[key]) {
        options[key] = {};
      }
    });

    items.forEach((item) => {
      filters.forEach(({ key }) => {
        const values = item.filterValues[key];
        if (!values || !Array.isArray(values)) return;

        const currentOptions = options[key] ?? {};
        values.forEach((v) => {
          currentOptions[v] = (currentOptions[v] || 0) + 1;
        });
        options[key] = currentOptions;
      });
    });

    return options;
  }, [items, filters]);

  const hasAnyFilterOptions = useMemo(() => {
    return filters.some(({ key }) => {
      const values = filterOptions[key];
      return values && Object.keys(values).length > 0;
    });
  }, [filters, filterOptions]);

  /**
   * Apply filters
   */
  useEffect(() => {
    const filtered = items.filter((item) => {
      return filters.every(({ key }) => {
        if (!activeFilters[key]?.length) return true;
        const itemValues = item.filterValues[key];
        if (!itemValues || !Array.isArray(itemValues)) return false;
        return activeFilters[key].some((v) => itemValues.includes(v));
      });
    });

    onFilterChange(filtered);
  }, [activeFilters, items, filters]);

  useEffect(() => {
    setActiveFilters(emptyFilters);
  }, [resetKey]);

  const toggleFilter = (key: FilterKey, value: string) => {
    setActiveFilters((prev) => {
      const current = prev[key] ?? [];
      const exists = current.includes(value);
      return {
        ...prev,
        [key]: exists ? current.filter((v) => v !== value) : [...current, value],
      };
    });
  };

  const clearFilters = () => {
    setActiveFilters(emptyFilters);
    onClearFilters();
  };

  const toggleMobileFilters = () => {
    setMobileFiltersOpen((prev) => !prev);
  };

  const anyFiltersActive = (activeFilters: FiltersState) => {
    return filters.some(({ key }) => (activeFilters[key]?.length ?? 0) > 0);
  };

  const mobileHeading = () => {
    if (!mobile) return null;
    return (
      <div
        onClick={toggleMobileFilters}
        className={cn(styles.mobileHeading, {
          [styles.mobileHeadingActive]: mobileFiltersOpen,
        })}
      >
        <h4>Filter</h4>
        <button>
          <Image
            src="/images/chevron-grey.svg"
            width={10}
            height={5}
            alt="Toggle button's icon"
          ></Image>
        </button>
      </div>
    );
  };

  if (!filters.length || !hasAnyFilterOptions) {
    return null;
  }

  return (
    <div className={styles.filterColumn}>
      <div className={cn(styles.filters)}>
        {mobileHeading()}
        <div
          className={cn({
            [styles.filtersMobile]: mobile,
            [styles.filtersMobileActive]: mobileFiltersOpen,
          })}
        >
          {filters.map(({ key, label }) => {
            const values = filterOptions[key] ?? {};

            if (!Object.keys(values).length) return null;

            return (
              <div key={key} className={styles.filterGroup}>
                <h5 className={styles.heading}>{label}:</h5>

                <ul className={styles.options}>
                  {Object.entries(values).map(([value, count]) => (
                    <li key={value}>
                      <label className={styles.checkbox}>
                        <input
                          type="checkbox"
                          checked={activeFilters[key]?.includes(value) ?? false}
                          onChange={() => toggleFilter(key, value)}
                        />
                        <span className={styles.box} />
                        <span>
                          {value} ({count})
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}

          {anyFiltersActive(activeFilters) ? (
            <div className={styles.clearButton}>
              <Button
                type="button"
                className={styles.clearButton}
                onClick={() => clearFilters()}
                label="Clear all filters"
                buttonStyle="secondary"
                hoverUp={false}
              ></Button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
